/**
 * Question-level analytics modal for the quiz collection editor.
 * Renders timing distribution, percentile breakdown, and accuracy split for a single question.
 *
 * Data is fetched from /quiz-collections/{collectionId}/questions/{questionId}/analytics
 * each time the modal opens; this keeps the editor list endpoint cheap and lets the
 * backend aggregate from QuizAttemptAnswer rows on demand.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Spin, Empty, Tag, Tooltip, message, Image } from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BarChartOutlined,
  LineChartOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Column, Pie } from '@ant-design/charts';
import quizCollectionsService from '../../services/quizCollectionsService';
import { getFileUrl } from '../../utils/urlUtils';
import { formatDurationFromMilliseconds } from '../../utils/timeUtils';
import styles from './QuestionAnalyticsModal.module.css';

const QUESTION_IMAGE_SIZE = 96;
const CHOICE_IMAGE_SIZE = 36;

/**
 * Format a millisecond value as a compact, human-readable label.
 *
 * @param {number|null|undefined} ms - Duration in milliseconds.
 * @returns {string} Formatted label or em-dash placeholder.
 */
const fmtMs = (ms) => {
  if (ms === null || ms === undefined) return '—';
  return formatDurationFromMilliseconds(Math.round(Number(ms)));
};

/**
 * Strip HTML markup and trim a long string for inline display.
 *
 * @param {string} html - Raw HTML body of a question.
 * @param {number} max - Maximum character count.
 * @returns {string} Plain-text excerpt.
 */
const plainExcerpt = (html, max = 220) => {
  if (!html) return '';
  const plain = String(html).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  return plain.length > max ? `${plain.slice(0, max)}…` : plain;
};

/**
 * Single KPI tile inside the modal header strip.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element} KPI tile.
 */
const KpiTile = ({ icon, label, value, accent, hint }) => (
  <div className={styles.kpiTile} style={accent ? { borderColor: accent } : undefined}>
    <div className={styles.kpiIcon} style={accent ? { color: accent } : undefined}>{icon}</div>
    <div className={styles.kpiBody}>
      <div className={styles.kpiValue}>{value}</div>
      <div className={styles.kpiLabel}>
        {label}
        {hint && (
          <Tooltip title={hint}>
            <InfoCircleOutlined className={styles.kpiHint} />
          </Tooltip>
        )}
      </div>
    </div>
  </div>
);

/**
 * Modal that visualizes per-question performance analytics.
 *
 * @param {object} props - Component props.
 * @param {boolean} props.open - Whether the modal is visible.
 * @param {Function} props.onClose - Close handler.
 * @param {number} props.collectionId - Owning collection id.
 * @param {object|null} props.question - Editor question object (used for header context).
 * @returns {JSX.Element|null} Analytics modal.
 */
const QuestionAnalyticsModal = ({ open, onClose, collectionId, question }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const questionId = question?.id ?? question?.questionId;
  const questionCode = question?.code ?? question?.questionCode;
  const questionBody = question?.body ?? question?.questionBody;
  const questionImageUrl = question?.imageUrl;

  useEffect(() => {
    if (!open || !questionId || !collectionId) return undefined;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setData(null);
        const result = await quizCollectionsService.getQuizQuestionAnalytics(
          collectionId,
          questionId,
        );
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          message.error(t('quiz.questionAnalytics.fetchError', 'Failed to load question analytics'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, questionId, collectionId, t]);

  const histogramData = useMemo(() => {
    if (!data?.durationBuckets) return [];
    return data.durationBuckets.map((b) => ({
      bucket: `${fmtMs(b.rangeStartMs)} – ${fmtMs(b.rangeEndMs)}`,
      count: b.count,
      rangeStartMs: b.rangeStartMs,
    }));
  }, [data]);

  const accuracyPieData = useMemo(() => {
    if (!data) return [];
    const correct = data.correctCount || 0;
    const incorrect = data.incorrectCount || 0;
    if (correct === 0 && incorrect === 0) return [];
    return [
      { type: t('quiz.statistics.correct', 'Correct'), value: correct },
      { type: t('quiz.statistics.incorrect', 'Incorrect'), value: incorrect },
    ];
  }, [data, t]);

  const accuracy = data?.accuracyPercent ?? 0;
  const accuracyColor = accuracy >= 70 ? '#52c41a' : accuracy >= 40 ? '#faad14' : '#ff4d4f';

  const histogramConfig = useMemo(() => ({
    data: histogramData,
    xField: 'bucket',
    yField: 'count',
    columnStyle: { radius: [4, 4, 0, 0] },
    color: '#1677ff',
    xAxis: {
      label: { autoRotate: true, autoHide: false, style: { fontSize: 11 } },
      title: { text: t('quiz.questionAnalytics.timePerAttempt', 'Time per attempt') },
    },
    yAxis: {
      title: { text: t('quiz.questionAnalytics.attempts', 'Attempts') },
      minInterval: 1,
    },
    tooltip: {
      formatter: (datum) => ({
        name: t('quiz.questionAnalytics.attempts', 'Attempts'),
        value: datum.count,
      }),
    },
    height: 240,
  }), [histogramData, t]);

  const pieConfig = useMemo(() => ({
    data: accuracyPieData,
    angleField: 'value',
    colorField: 'type',
    radius: 0.9,
    innerRadius: 0.6,
    color: ['#52c41a', '#ff4d4f'],
    statistic: {
      title: {
        offsetY: -4,
        style: { fontSize: '12px', color: '#8c8c8c' },
        content: t('quiz.questionAnalytics.successRate', 'Success rate'),
      },
      content: {
        offsetY: 4,
        style: { fontSize: '20px', fontWeight: 700, color: accuracyColor },
        content: `${accuracy.toFixed(1)}%`,
      },
    },
    legend: { position: 'bottom' },
    height: 220,
  }), [accuracyPieData, accuracy, accuracyColor, t]);

  const noData = !loading && data && data.totalAnswered === 0;
  const noTiming = !loading && data && data.timingSamples === 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={860}
      destroyOnClose
      title={
        <div className={styles.modalTitle}>
          <BarChartOutlined />
          <span>{t('quiz.questionAnalytics.title', 'Question Analytics')}</span>
          {questionCode && <Tag color="blue" style={{ marginLeft: 8 }}>{questionCode}</Tag>}
        </div>
      }
    >
      {(data?.imageUrl || data?.questionBody || questionBody) && (
        <div className={styles.questionHero}>
          {(data?.imageUrl || questionImageUrl) && (
            <div className={styles.questionHeroImageWrap}>
              <Image
                src={getFileUrl(data?.imageUrl || questionImageUrl)}
                alt=""
                width={QUESTION_IMAGE_SIZE}
                height={QUESTION_IMAGE_SIZE}
                className={styles.questionHeroImage}
                preview={false}
              />
            </div>
          )}
          <div className={styles.questionHeroBody}>
            <div className={styles.questionHeroLabel}>
              {t('quiz.questionAnalytics.questionLabel', 'Question')}
            </div>
            <div
              className={styles.questionExcerpt}
              dangerouslySetInnerHTML={{ __html: data?.questionBody || questionBody || '' }}
            />
          </div>
        </div>
      )}

      {loading && (
        <div className={styles.loadingWrap}>
          <Spin />
        </div>
      )}

      {!loading && data && noData && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('quiz.questionAnalytics.noData', 'No play data yet for this question')}
          style={{ padding: '32px 0' }}
        />
      )}

      {!loading && data && !noData && (
        <>
          <div className={styles.kpiRow}>
            <KpiTile
              icon={<CheckCircleOutlined />}
              label={t('quiz.questionAnalytics.successRate', 'Success rate')}
              value={`${accuracy.toFixed(1)}%`}
              accent={accuracyColor}
            />
            <KpiTile
              icon={<ClockCircleOutlined />}
              label={t('quiz.questionAnalytics.medianTime', 'Median time')}
              value={fmtMs(data.medianDurationMs)}
              hint={t(
                'quiz.questionAnalytics.medianHint',
                'Half of attempts finished faster than this value.',
              )}
            />
            <KpiTile
              icon={<LineChartOutlined />}
              label={t('quiz.questionAnalytics.attempts', 'Attempts')}
              value={data.totalAnswered}
            />
            <KpiTile
              icon={<CloseCircleOutlined />}
              label={t('quiz.questionAnalytics.incorrect', 'Incorrect')}
              value={data.incorrectCount}
              accent="#ff4d4f"
            />
          </div>

          <div className={styles.percentileRow}>
            <div className={styles.percentileChip}>
              <span className={styles.percentileLabel}>P25</span>
              <span className={styles.percentileValue}>{fmtMs(data.p25DurationMs)}</span>
            </div>
            <div className={styles.percentileChip}>
              <span className={styles.percentileLabel}>P50</span>
              <span className={styles.percentileValue}>{fmtMs(data.medianDurationMs)}</span>
            </div>
            <div className={styles.percentileChip}>
              <span className={styles.percentileLabel}>P75</span>
              <span className={styles.percentileValue}>{fmtMs(data.p75DurationMs)}</span>
            </div>
            <div className={styles.percentileChip}>
              <span className={styles.percentileLabel}>{t('quiz.questionAnalytics.mean', 'Mean')}</span>
              <span className={styles.percentileValue}>{fmtMs(data.meanDurationMs)}</span>
            </div>
            <div className={styles.percentileChip}>
              <span className={styles.percentileLabel}>{t('quiz.questionAnalytics.range', 'Range')}</span>
              <span className={styles.percentileValue}>
                {fmtMs(data.minDurationMs)} – {fmtMs(data.maxDurationMs)}
              </span>
            </div>
          </div>

          <div className={styles.chartsRow}>
            <div className={styles.chartCard}>
              <div className={styles.chartTitle}>
                <BarChartOutlined />
                {t('quiz.questionAnalytics.timeDistribution', 'Play-time distribution')}
              </div>
              {noTiming ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t(
                    'quiz.questionAnalytics.noTiming',
                    'No timing samples recorded for this question yet.',
                  )}
                />
              ) : (
                <Column {...histogramConfig} />
              )}
            </div>

            <div className={styles.chartCardNarrow}>
              <div className={styles.chartTitle}>
                <CheckCircleOutlined />
                {t('quiz.questionAnalytics.accuracyBreakdown', 'Accuracy breakdown')}
              </div>
              {accuracyPieData.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <Pie {...pieConfig} />
              )}
            </div>
          </div>

          {(data.meanDurationCorrectMs !== null || data.meanDurationIncorrectMs !== null) && (
            <div className={styles.compareRow}>
              <div className={`${styles.compareCard} ${styles.compareCorrect}`}>
                <span className={styles.compareLabel}>
                  <CheckCircleOutlined /> {t('quiz.questionAnalytics.avgCorrect', 'Avg. time when correct')}
                </span>
                <span className={styles.compareValue}>{fmtMs(data.meanDurationCorrectMs)}</span>
              </div>
              <div className={`${styles.compareCard} ${styles.compareIncorrect}`}>
                <span className={styles.compareLabel}>
                  <CloseCircleOutlined /> {t('quiz.questionAnalytics.avgIncorrect', 'Avg. time when incorrect')}
                </span>
                <span className={styles.compareValue}>{fmtMs(data.meanDurationIncorrectMs)}</span>
              </div>
            </div>
          )}

          {data.choices?.length > 0 && (
            <div className={styles.choiceStatsSection}>
              <div className={styles.choiceStatsHeader}>
                <div className={styles.chartTitle}>
                  <LineChartOutlined />
                  {t('quiz.questionAnalytics.choiceClicks', 'Choice interaction')}
                </div>
                <div className={styles.choiceStatsSummary}>
                  {t('quiz.questionAnalytics.totalChoiceClicks', 'Total clicks')}: <strong>{data.totalChoiceClicks}</strong>
                </div>
              </div>

              <div className={styles.choiceStatsList}>
                {data.choices.map((choice, index) => (
                  <div key={choice.choiceId ?? index} className={styles.choiceStatsRow}>
                    <div className={styles.choiceStatsInfo}>
                      <span className={styles.choiceOrderBadge}>{index + 1}</span>
                      {choice.imageUrl && (
                        <Image
                          src={getFileUrl(choice.imageUrl)}
                          alt=""
                          width={CHOICE_IMAGE_SIZE}
                          height={CHOICE_IMAGE_SIZE}
                          className={styles.choiceStatsImage}
                          preview={false}
                        />
                      )}
                      <div className={styles.choiceStatsTextBlock}>
                        <div className={styles.choiceStatsPrimary}>
                          <span className={styles.choiceStatsText}>
                            {choice.text || t('quiz.statistics.imageChoice', '(Image)')}
                          </span>
                          {choice.isCorrect && (
                            <Tag color="green" className={styles.choiceStatsCorrectTag}>
                              {t('quiz.statistics.correct', 'Correct')}
                            </Tag>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={styles.choiceStatsMetrics}>
                      <div className={styles.choiceStatsMetricLine}>
                        <span>{choice.clickCount} {t('quiz.questionAnalytics.clicks', 'clicks')}</span>
                        <strong>{(choice.clickPercent ?? 0).toFixed(1)}%</strong>
                      </div>
                      <div className={styles.choiceStatsBarTrack}>
                        <div
                          className={styles.choiceStatsBarFill}
                          style={{ width: `${Math.min(100, Math.max(0, choice.clickPercent ?? 0))}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default QuestionAnalyticsModal;
